import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { replyFromInbox, toggleMarkAsRead } from 'API/linkedin-funnel';
import { ErrorMessages, ProductGroups, ZoomLevels } from 'enums';
import AutoSyncProcessingDialog from 'features/co-pilot/auto-sync-processing-dialog';
import AutoSyncWarningDialog from 'features/co-pilot/auto-sync-warning-dialog';
import { FunnelTypes } from 'features/co-pilot/co-pilot.types';
import useCheckLinkedinAccountStatus from 'features/co-pilot/use-check-linkedin-account-status';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDatePicker from 'features/ui/base-components/pai-date-picker';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import ContactDrawer from '../campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import useContactDrawer from '../campaign-builder/use-contact-drawer';
import { useLinkedinAccountsQuery } from '../linkedin-accounts/hooks';
import CampaignList from './campaign-list';
import ContactMessages from './contact-messages';
import ContactMessagesPlaceholder from './contact-messages-placeholder';
import { FilterOptions, MessageTypes } from './funnel-inbox.types';
import InboxHeader from './inbox-header';
import { useInboxCampaigns } from './use-inbox-campaigns';
import { useInboxContacts } from './use-inbox-contacts';
import { useInboxMessages } from './use-inbox-messages';
import { useInboxUpdate } from './use-inbox-update';

interface Props {
  productGroup: ProductGroups;
}

const FunnelInbox: React.FC<Props> = ({ productGroup }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  useUserData();

  const {
    autoSyncLoading,
    autoSyncProcessingDialog,
    autoSyncWarningDialog,
    autoSyncSuccessful,
    handleCloseAutoSyncSuccessfulSnackbar,
    handleCloseAutoSyncProcessingDialog,
    handleCloseAutoSyncWarningDialog,
    startCheckAccountStatusInterval,
  } = useCheckLinkedinAccountStatus();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const type =
    productGroup === ProductGroups.HR_BOOSTER
      ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      : productGroup === ProductGroups.LEAD_BOOSTER
        ? FunnelTypes.SOCIAL_SELLING_CO_PILOT
        : undefined;

  const [searchParam, setSearchParam] = useState('');
  const [searchFilters, setSearchFilters] = useState<string[]>([FilterOptions.ACTIVE_CAMPAIGNS]);

  const {
    data: linkedinAccounts = [],
    error: linkedinAccountsError,
  } = useLinkedinAccountsQuery({ prioritizeCurrentUser: true });
  const [liAccountsInitiated, setLiAccountsInitiated] = useState(false);
  const [selectedLinkedinAccounts, setSelectedLinkedinAccounts] = useState<string[]>([]);

  const {
    isLoading: campaignsLoading,
    campaigns,
    totalCampaigns,
    totalUnreadMessages,
  } = useInboxCampaigns(type, liAccountsInitiated, searchParam, searchFilters, selectedLinkedinAccounts, resetCampaignSelection);

  const [selectedCampaign, setSelectedCampaign] = useState<string | false>(false);

  const { isLoading: contactsLoading, contacts } = useInboxContacts(selectedCampaign);
  const [selectedContact, setSelectedContact] = useState<string | false>(false);
  const [markAsUnreadLoading, setMarkAsUnreadLoading] = useState(false);

  const { isLoading: messagesLoading, messages } = useInboxMessages(selectedCampaign, selectedContact);

  const [chatMessageFieldInput, setChatMessageFieldInput] = useState('');
  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const [messageSentSuccessSnackbarOpen, setMessageSentSuccessSnackbarOpen] = useState(false);

  const { isUpdating: updateInboxLoading, progress: updateProgress, triggerUpdate } = useInboxUpdate({
    onComplete: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inboxCampaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['inboxContacts'] });
      await queryClient.invalidateQueries({ queryKey: ['inboxMessages'] });
    },
  });

  const { contactDrawer, handleOpenContactDrawer, handleUpdateDrawerContactIsActive, handleCloseContactDrawer } = useContactDrawer();

  const fullCampaign = useMemo(() => campaigns.find((campaign) => campaign._id === selectedCampaign), [selectedCampaign, campaigns]);
  const fullContact = useMemo(() => contacts.find((contact) => contact.profileId === selectedContact), [selectedContact, contacts]);

  useEffect(() => {
    if (!_.isNil(linkedinAccountsError)) {
      handleOpenErrorDialog(linkedinAccountsError, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
  }, [linkedinAccountsError, handleOpenErrorDialog]);

  // Einmal beim ersten Laden. Vorher lief das bei jeder leeren Auswahl erneut: wer alle Konten
  // abwählte, bekam beim nächsten Neuladen der Kontenliste wieder das erste Konto.
  useEffect(() => {
    if (!liAccountsInitiated && !_.isEmpty(linkedinAccounts)) {
      const inboxSettings = localStorage.getItem('inbox-settings');

      if (!_.isNil(inboxSettings)) {
        let parsedSettings: { funnelId?: string; liAccountId?: string } = {};
        try {
          parsedSettings = JSON.parse(inboxSettings);
        } catch {
          localStorage.removeItem('inbox-settings');
        }
        const { funnelId, liAccountId } = parsedSettings;

        if (!_.isNil(liAccountId)) {
          setSelectedLinkedinAccounts([liAccountId]);
          setSearchFilters([FilterOptions.ACTIVE_CAMPAIGNS, FilterOptions.NOT_ACTIVE_CAMPAIGNS]);

          if (!_.isNil(funnelId)) {
            setSelectedCampaign(funnelId);
            (async () => {
              await queryClient.invalidateQueries({ queryKey: ['inboxContacts'] });
            })();
          }
        }

        localStorage.removeItem('inbox-settings');
      } else {
        // Voreinstellung: alle Konten (bisher nur das erste).
        setSelectedLinkedinAccounts(linkedinAccounts.map((account) => account._id));
      }

      setLiAccountsInitiated(true);
    }
  }, [linkedinAccounts, liAccountsInitiated]);

  function resetCampaignSelection() {
    setSelectedCampaign(false);
    setSelectedContact(false);
  }

  /**
   * Search campaigns handlers
   */

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  /**
   * Filter campaigns handlers
   */

  const handleSearchFiltersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    if (newValue.length > 0) {
      setSearchFilters(newValue);
    }
  };

  const handleSelectedLinkedinAccountChange = (id: string) => {
    return () => {
      const updatedSelectedLinkedinAccounts = [...selectedLinkedinAccounts];

      if (updatedSelectedLinkedinAccounts.includes(id)) {
        _.remove(updatedSelectedLinkedinAccounts, (accountId) => accountId === id);
      } else {
        updatedSelectedLinkedinAccounts.push(id);
      }

      setSelectedLinkedinAccounts(updatedSelectedLinkedinAccounts);
    };
  };

  const handleSelectAllLinkedinAccounts = () => {
    const linkedinAccountIds = linkedinAccounts.map((account) => account._id);
    setSelectedLinkedinAccounts(linkedinAccountIds);
  };

  const handleUnselectAllLinkedinAccounts = () => {
    setSelectedLinkedinAccounts([]);
  };

  /**
   * Campaign selection handlers
   */

  const handleSelectedCampaignChange = (value: string) => {
    return () => {
      setSelectedContact(false);
      setSelectedCampaign((prev) => (value !== prev ? value : false));
    };
  };

  /**
   * Contact selection handlers
   */

  const handleMarkAsUnread = async () => {
    if (!selectedCampaign) return;
    if (!type) return;
    if (!selectedContact) return;

    setMarkAsUnreadLoading(true);

    const res = await toggleMarkAsRead(selectedCampaign, selectedContact, false);

    if (!res.error) {
      await queryClient.invalidateQueries({ queryKey: ['inboxContacts'] });
      await queryClient.invalidateQueries({ queryKey: ['inboxCampaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['taskCounts'] });

      setMarkAsUnreadLoading(false);
    } else {
      setMarkAsUnreadLoading(false);
      handleOpenErrorDialog(res.error, '/messages/inbox/:campaignId/contact/:profileId/toggleMarkAsRead', 'put');
    }
  };

  const handleSelectedContactChange = (value: string | undefined) => {
    return async () => {
      if (!selectedCampaign) return;
      if (!type) return;
      if (!value) return;

      setSelectedContact((prev) => (value !== prev ? value : false));

      if (value !== selectedContact) {
        const foundContact = contacts.find((contact) => contact.profileId === value);

        if (!_.isNil(foundContact) && foundContact.hasUnreadMessages) {
          const resMarkAsRead = await toggleMarkAsRead(selectedCampaign, value, true);

          if (!resMarkAsRead.error) {
            await queryClient.invalidateQueries({ queryKey: ['inboxContacts'] });
            await queryClient.invalidateQueries({ queryKey: ['inboxCampaigns'] });
            await queryClient.invalidateQueries({ queryKey: ['tasks'] });
            await queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
          } else {
            handleOpenErrorDialog(resMarkAsRead.error, '/messages/inbox/:campaignId/contact/:profileId/toggleMarkAsRead', 'put');
          }
        }
      }
    };
  };

  /**
   * Chat message field handlers
   */

  const handleChatMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChatMessageFieldInput(event.target.value);
  };

  const handleSendMessage = async () => {
    if (!selectedCampaign) return;
    if (!selectedContact) return;

    setSendMessageLoading(true);

    const currentContact = contacts.find((contact) => contact.profileId === selectedContact);

    const lastMessage = _.last(messages);
    const { messageType = MessageTypes.LINKEDIN, text = '' } = lastMessage || {};
    const linkedInUrl = currentContact?.linkedInURL;

    if (!text) {
      setSendMessageLoading(false);
      return;
    }

    const res = await replyFromInbox(selectedContact, selectedCampaign, chatMessageFieldInput, messageType, text, linkedInUrl);

    if (!res.error) {
      setChatMessageFieldInput('');
      handleOpenMessageSentSuccessSnackbar();
      await queryClient.invalidateQueries({ queryKey: ['inboxMessages'] });
    } else {
      if (res.error.message === ErrorMessages.ACCOUNT_OUT_OF_SYNC) {
        if (!_.isNil(fullCampaign?.linkedinAccount._id)) {
          startCheckAccountStatusInterval(fullCampaign?.linkedinAccount._id);
        }
      } else {
        handleOpenErrorDialog(res.error, '/messages/reply', 'post');
      }
    }

    setSendMessageLoading(false);
  };

  const handleOpenMessageSentSuccessSnackbar = () => {
    setMessageSentSuccessSnackbarOpen(true);
  };

  const handleCloseMessageSentSuccessSnackbar = () => {
    setMessageSentSuccessSnackbarOpen(false);
  };

  // ── Inbox Update Modal ────────────────────────────────────────────────────
  const [inboxUpdateModalOpen, setInboxUpdateModalOpen] = useState(false);
  const [inboxUpdateSinceMode, setInboxUpdateSinceMode] = useState<'last' | 'custom'>('last');
  const [inboxUpdateSinceDate, setInboxUpdateSinceDate] = useState<Date>(
    new Date(Date.now() - 21 * 24 * 60 * 60 * 1000),
  );

  const handleUpdateInbox = () => {
    if (!type) return;
    if (selectedLinkedinAccounts.length === 0) return;
    setInboxUpdateSinceMode('last');
    setInboxUpdateSinceDate(new Date(Date.now() - 21 * 24 * 60 * 60 * 1000));
    setInboxUpdateModalOpen(true);
  };

  const handleInboxUpdateConfirm = () => {
    setInboxUpdateModalOpen(false);
    const since =
      inboxUpdateSinceMode === 'custom'
        ? inboxUpdateSinceDate.toISOString()
        : undefined;
    triggerUpdate(type!, searchParam, searchFilters, selectedLinkedinAccounts, since);
  };

  return (
    <>
      {/* ── Inbox Update Modal ── */}
      <Dialog open={inboxUpdateModalOpen} onClose={() => setInboxUpdateModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>{t('inboxUpdateModalTitle')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('inboxUpdateModalDescription')}
          </Typography>
          <RadioGroup
            value={inboxUpdateSinceMode}
            onChange={(e) => setInboxUpdateSinceMode(e.target.value as 'last' | 'custom')}
          >
            <FormControlLabel value="last" control={<Radio size="small" />} label={t('inboxUpdateSinceLast')} />
            <FormControlLabel value="custom" control={<Radio size="small" />} label={t('inboxUpdateSinceCustom')} />
          </RadioGroup>
          {inboxUpdateSinceMode === 'custom' && (
            <Stack sx={{ mt: 1.5 }}>
              <PaiDatePicker
                label={t('inboxUpdateSinceDate')}
                value={inboxUpdateSinceDate}
                maxDate={new Date()}
                onChange={(date) => { if (date) setInboxUpdateSinceDate(date as Date); }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <PaiButton
            text={t('cancel')}
            border="rounded"
            size="small"
            color="secondary"
            action={() => setInboxUpdateModalOpen(false)}
          />
          <PaiButton
            text={t('inboxUpdateModalConfirm')}
            border="rounded"
            size="small"
            action={handleInboxUpdateConfirm}
          />
        </DialogActions>
      </Dialog>

      <div className={`${classes.container} ${updateInboxLoading ? classes.updating : ''}`}>
        <div className={classes.header}>
          <InboxHeader
            searchParam={searchParam}
            searchFilters={searchFilters}
            linkedinAccounts={linkedinAccounts}
            activeLinkedinAccounts={selectedLinkedinAccounts}
            updateInboxLoading={updateInboxLoading}
            updateProgress={updateProgress}
            handleSearchParamChange={updateInboxLoading ? () => { } : handleSearchParamChange}
            handleSearchFiltersChange={updateInboxLoading ? () => { } : handleSearchFiltersChange}
            handleSelectAllLinkedinAccounts={updateInboxLoading ? () => { } : handleSelectAllLinkedinAccounts}
            handleUnselectAllLinkedinAccounts={updateInboxLoading ? () => { } : handleUnselectAllLinkedinAccounts}
            handleSelectedLinkedinAccountChange={updateInboxLoading ? () => () => { } : handleSelectedLinkedinAccountChange}
            handleUpdateInbox={handleUpdateInbox}
          />
        </div>
        <div className={classes.content}>
          <CampaignList
            campaigns={campaigns}
            campaignsLoading={campaignsLoading || updateInboxLoading}
            totalCampaigns={totalCampaigns}
            totalUnreadMessages={totalUnreadMessages}
            selectedCampaign={selectedCampaign}
            sendMessageLoading={sendMessageLoading}
            contactsLoading={contactsLoading || updateInboxLoading}
            contacts={contacts}
            selectedContact={selectedContact}
            handleSelectedCampaignChange={updateInboxLoading ? () => () => { } : handleSelectedCampaignChange}
            handleSelectedContactChange={updateInboxLoading ? () => () => { } : handleSelectedContactChange}
          />
          <div className={classes.messages}>
            {selectedContact ? (
              <ContactMessages
                fullCampaign={fullCampaign}
                fullContact={fullContact}
                messages={messages}
                messagesLoading={messagesLoading || updateInboxLoading}
                markAsUnreadLoading={markAsUnreadLoading}
                autoSyncLoading={autoSyncLoading || updateInboxLoading}
                chatMessageFieldInput={chatMessageFieldInput}
                sendMessageLoading={sendMessageLoading || updateInboxLoading}
                handleChatMessageChange={updateInboxLoading ? () => { } : handleChatMessageChange}
                handleSendMessage={updateInboxLoading ? async () => { } : handleSendMessage}
                handleMarkAsUnread={updateInboxLoading ? async () => { } : handleMarkAsUnread}
                handleOpenQualifiedContactPreviewDrawer={updateInboxLoading ? () => () => { } : handleOpenContactDrawer}
              />
            ) : (
              <ContactMessagesPlaceholder />
            )}
          </div>
        </div>
      </div>

      <PaiSuccessAlert
        open={messageSentSuccessSnackbarOpen}
        message={t('messageSentSuccess')}
        position={'bottom'}
        handleClose={handleCloseMessageSentSuccessSnackbar}
      />
      <AutoSyncProcessingDialog
        open={autoSyncProcessingDialog.open}
        syncStage={autoSyncProcessingDialog.syncStage}
        handleClose={handleCloseAutoSyncProcessingDialog}
      />
      <AutoSyncWarningDialog
        open={autoSyncWarningDialog.open}
        status={autoSyncWarningDialog.status}
        accountId={autoSyncWarningDialog.accountId}
        linkedinAccounts={linkedinAccounts}
        handleClose={handleCloseAutoSyncWarningDialog}
      />
      <PaiSuccessAlert
        open={autoSyncSuccessful}
        message={t('linkedinAccountSuccessfullySignedIn')}
        position={'bottom'}
        handleClose={handleCloseAutoSyncSuccessfulSnackbar}
      />
      <ContactDrawer
        open={contactDrawer.open}
        productGroup={productGroup}
        funnelId={contactDrawer.funnelId}
        contactIndex={contactDrawer.contactIndex}
        profileId={contactDrawer.profileId}
        aiJudgement={contactDrawer.aiJudgement}
        aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
        assessment={contactDrawer.assessment}
        percentiles={contactDrawer.percentiles}
        isActive={contactDrawer.isActive}
        contactDetails={contactDrawer.contactDetails}
        aboutContact={contactDrawer.aboutContact}
        aboutCompany={contactDrawer.aboutCompany}
        handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
        handleCloseDrawer={handleCloseContactDrawer}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  updating: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: '6px',
    },
    '& > *': {
      position: 'relative',
      zIndex: 2,
    },
  },
  header: {
    padding: '24px 32px 16px 32px',
  },
  content: {
    flex: 1,
    minHeight: 0,
    padding: '0 32px 32px 32px',
    display: 'flex',
    gap: '16px',
    overflow: 'hidden',
  },
  messages: {
    flex: 1,
    minWidth: 0,
  },
}));

export default FunnelInbox;
